class VoiceService {
  constructor() {
    this.recognition = null;
    this.synthesis = window.speechSynthesis;
    this.isListening = false;
    this.onResult = null;
    this.onError = null;
    this.onStart = null;
    this.onEnd = null;
    this._shouldSpeak = true; // Mặc định cho phép phát âm
    
    this._initializeRecognition();
    this._preloadVoices();
  }

  // Preload voices để đảm bảo sẵn sàng khi cần
  _preloadVoices() {
    // Gọi getVoices() để trigger load voices
    this.getVoices().then(voices => {
      console.log('Voices preloaded:', voices.length, 'voices available');
      const viVoices = voices.filter(v => 
        v.lang.toLowerCase().includes('vi') || 
        v.name.toLowerCase().includes('vietnamese')
      );
      if (viVoices.length > 0) {
        console.log('✅ Vietnamese voices found:', viVoices.map(v => v.name));
      } else {
        console.warn('⚠️ No Vietnamese voices found. System may use default voice with vi-VN lang.');
      }
    });
  }

  _initializeRecognition() {
    // Kiểm tra browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('Speech Recognition không được hỗ trợ trên browser này');
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = false; // Dừng sau khi nói xong
    this.recognition.interimResults = false; // Chỉ trả về kết quả cuối cùng
    this.recognition.lang = 'vi-VN'; // Tiếng Việt (hoặc 'en-US' cho tiếng Anh)
    this.recognition.maxAlternatives = 1;

    // Event handlers
    this.recognition.onstart = () => {
      this.isListening = true;
      if (this.onStart) this.onStart();
    };

    this.recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (this.onResult) {
        this.onResult(transcript);
      }
    };

    this.recognition.onerror = (event) => {
      this.isListening = false;
      let errorMessage = 'Lỗi nhận diện giọng nói';
      
      switch(event.error) {
        case 'no-speech':
          errorMessage = 'Không phát hiện giọng nói. Vui lòng thử lại.';
          break;
        case 'audio-capture':
          errorMessage = 'Không thể truy cập microphone.';
          break;
        case 'not-allowed':
          errorMessage = 'Quyền truy cập microphone bị từ chối. Vui lòng cho phép trong cài đặt browser.';
          break;
        case 'network':
          errorMessage = 'Lỗi kết nối mạng.';
          break;
        case 'aborted':
          // User đã dừng, không cần hiển thị lỗi
          return;
        default:
          errorMessage = `Lỗi: ${event.error}`;
      }
      
      if (this.onError) {
        this.onError(errorMessage);
      }
    };

    this.recognition.onend = () => {
      this.isListening = false;
      if (this.onEnd) this.onEnd();
    };
  }

  // Bắt đầu nghe
  startListening() {
    if (!this.recognition) {
      if (this.onError) {
        this.onError('Speech Recognition không được hỗ trợ trên browser này. Vui lòng dùng Chrome, Edge hoặc Safari.');
      }
      return;
    }

    if (this.isListening) {
      this.stopListening();
      return;
    }

    try {
      this.recognition.start();
    } catch (error) {
      console.error('Error starting recognition:', error);
      if (this.onError) {
        this.onError('Không thể bắt đầu nhận diện giọng nói. Vui lòng thử lại.');
      }
    }
  }

  // Dừng nghe
  stopListening() {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
    }
  }

  // Phát âm văn bản sử dụng Google Translate TTS (fallback khi không có giọng tiếng Việt)
  async _speakWithGoogleTTS(text) {
    try {
      console.log('🔊 Attempting to use Google Translate TTS for Vietnamese...');
      console.log('🔊 Text to speak:', text.substring(0, 100) + (text.length > 100 ? '...' : ''));
      
      // Loại bỏ emoji và ký tự đặc biệt để tránh lỗi
      const cleanText = text.replace(/[✅❌⚠️📊🔊🎤💡]/g, '').trim();
      
      // Chia text thành các đoạn ngắn (Google TTS có giới hạn ~200 ký tự)
      const maxLength = 200;
      const textChunks = [];
      let currentChunk = '';
      
      const words = cleanText.split(' ');
      for (const word of words) {
        if ((currentChunk + ' ' + word).length <= maxLength) {
          currentChunk += (currentChunk ? ' ' : '') + word;
        } else {
          if (currentChunk) textChunks.push(currentChunk);
          currentChunk = word;
        }
      }
      if (currentChunk) textChunks.push(currentChunk);
      
      console.log(`📝 Split text into ${textChunks.length} chunks`);
      
      // Phát từng đoạn tuần tự
      for (let i = 0; i < textChunks.length; i++) {
        const chunk = textChunks[i];
        console.log(`🔄 Playing chunk ${i + 1}/${textChunks.length}: "${chunk.substring(0, 50)}..."`);
        
        await this._playGoogleTTSChunk(chunk);
        
        // Đợi một chút giữa các đoạn (trừ đoạn cuối)
        if (i < textChunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      
      console.log('✅ Google TTS completed successfully');
    } catch (error) {
      console.error('❌ Google TTS error:', error);
      throw error;
    }
  }

  // Phát một đoạn text bằng Google TTS
  async _playGoogleTTSChunk(text) {
    return new Promise((resolve, reject) => {
      let audio = null;
      let useDirectGoogle = false;
      let resolved = false;
      
      const cleanup = () => {
        if (audio && audio.parentNode) {
          audio.parentNode.removeChild(audio);
        }
      };
      
      const tryPlay = (url, isDirect = false) => {
        audio = new Audio();
        audio.volume = 1.0;
        audio.muted = false;
        audio.src = url;
        
        // Thêm vào DOM
        audio.style.display = 'none';
        document.body.appendChild(audio);
        
        audio.onended = () => {
          console.log('✅ Chunk finished playing');
          cleanup();
          if (!resolved) {
            resolved = true;
            resolve();
          }
        };
        
        audio.onerror = (error) => {
          console.error('❌ Audio error:', error);
          cleanup();
          
          // Nếu backend proxy lỗi và chưa thử direct Google, thử lại
          if (!isDirect && !resolved) {
            console.warn('⚠️ Backend proxy failed, trying direct Google TTS...');
            const googleTTSUrl = `https://translate.google.com/translate_tts?ie=UTF-8&tl=vi&client=tw-ob&q=${encodeURIComponent(text)}`;
            setTimeout(() => tryPlay(googleTTSUrl, true), 100);
          } else if (!resolved) {
            resolved = true;
            reject(new Error('Failed to play Google TTS audio'));
          }
        };
        
        audio.oncanplay = () => {
          console.log('✅ Audio ready to play');
          audio.play().catch(err => {
            console.error('❌ Failed to play:', err);
            if (!resolved) {
              resolved = true;
              cleanup();
              reject(err);
            }
          });
        };
        
        audio.load();
      };
      
      try {
        // Thử backend proxy trước (tránh CORS)
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        const proxyUrl = `${backendUrl}/api/tts?text=${encodeURIComponent(text)}&lang=vi`;
        tryPlay(proxyUrl, false);
      } catch (error) {
        console.error('❌ Google TTS setup error:', error);
        if (!resolved) {
          resolved = true;
          reject(error);
        }
      }
    });
  }

  // Set flag để kiểm tra có nên phát âm không
  setShouldSpeak(shouldSpeak) {
    this._shouldSpeak = shouldSpeak;
    console.log(`🔊 TTS ${shouldSpeak ? 'enabled' : 'disabled'}`);
    if (!shouldSpeak) {
      // Nếu tắt, dừng phát âm hiện tại
      this.stopSpeaking();
    }
  }

  // Phát âm văn bản
  async speak(text, options = {}) {
    // Kiểm tra flag trước khi phát âm
    if (!this._shouldSpeak) {
      console.log('🔇 TTS is disabled, skipping speech');
      return;
    }
    
    console.log('🎤 speak() called with text:', text.substring(0, 100) + (text.length > 100 ? '...' : ''));
    
    // Kiểm tra text có rỗng không
    if (!text || text.trim().length === 0) {
      console.warn('⚠️ Empty text, skipping speech');
      return;
    }

    // Dừng phát âm trước đó nếu có
    this.stopSpeaking();

    // Đảm bảo voices đã được load
    let voices = this.synthesis.getVoices();
    console.log(`📊 Current voices count: ${voices.length}`);
    
    // Nếu chưa có voices, đợi load
    if (voices.length === 0) {
      console.log('⏳ Waiting for voices to load...');
      voices = await this.getVoices();
      console.log(`📊 Loaded voices count: ${voices.length}`);
    }
    
    // Tìm giọng nói tiếng Việt với nhiều pattern
    const vietnameseVoice = voices.find(voice => {
      const lang = voice.lang.toLowerCase();
      const name = voice.name.toLowerCase();
      return lang.includes('vi') || 
             lang === 'vi-vn' ||
             lang === 'vi' ||
             name.includes('vietnamese') ||
             name.includes('viet nam') ||
             name.includes('vietnam');
    });

    console.log('🔍 Vietnamese voice search result:', vietnameseVoice ? `Found: ${vietnameseVoice.name} (${vietnameseVoice.lang})` : 'Not found');

    // Nếu không tìm thấy giọng tiếng Việt, thử Google TTS
    if (!vietnameseVoice) {
      console.log('⚠️ No Vietnamese voice found, trying Google Translate TTS as fallback...');
      try {
        await this._speakWithGoogleTTS(text);
        console.log('✅ Google TTS completed successfully');
        return; // Thành công, dừng ở đây
      } catch (error) {
        // Google TTS lỗi, không nên fallback về giọng mặc định
        // vì sẽ đọc sai tiếng Việt
        console.error('❌ Google TTS failed:', error);
        console.warn('💡 Solution: Install Vietnamese voice pack in Windows Settings > Time & Language > Speech');
        console.warn('💡 Or check your internet connection and backend server');
        // Không đọc nữa để tránh đọc sai
        return;
      }
    }

    // Sử dụng Web Speech API với giọng tiếng Việt
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Set giọng và lang
    utterance.voice = vietnameseVoice;
    utterance.lang = vietnameseVoice.lang || 'vi-VN';
    console.log('✅ Using Vietnamese voice:', vietnameseVoice.name, 'lang:', utterance.lang);
    
    utterance.rate = options.rate || 0.9; // Tốc độ hơi chậm hơn cho tiếng Việt
    utterance.pitch = options.pitch || 1.0; // Cao độ (0 - 2)
    utterance.volume = options.volume || 1.0; // Âm lượng (0 - 1)

    utterance.onerror = (event) => {
      console.error('❌ Speech synthesis error:', event);
      // Nếu Web Speech API lỗi, thử Google TTS
      if (event.error === 'not-allowed' || event.error === 'synthesis-failed') {
        console.log('⚠️ Web Speech API failed, trying Google TTS...');
        this._speakWithGoogleTTS(text).catch(err => {
          console.error('❌ Both Web Speech API and Google TTS failed:', err);
        });
      }
    };

    utterance.onstart = () => {
      console.log('🔊 Started speaking:', text.substring(0, 50) + '...');
    };

    utterance.onend = () => {
      console.log('✅ Finished speaking');
    };

    this.synthesis.speak(utterance);
  }

  // Dừng phát âm
  stopSpeaking() {
    if (this.synthesis.speaking) {
      this.synthesis.cancel();
    }
  }

  // Kiểm tra đang nói
  isSpeaking() {
    return this.synthesis.speaking;
  }

  // Kiểm tra browser support
  isSupported() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    return !!SpeechRecognition;
  }

  // Lấy danh sách giọng nói có sẵn
  getVoices() {
    return new Promise((resolve) => {
      let voices = this.synthesis.getVoices();
      
      // Nếu đã có voices, trả về ngay
      if (voices.length > 0) {
        resolve(voices);
        return;
      }
      
      // Nếu chưa có, đợi event onvoiceschanged
      let timeoutId;
      const onVoicesChanged = () => {
        voices = this.synthesis.getVoices();
        if (voices.length > 0) {
          this.synthesis.onvoiceschanged = null; // Xóa handler
          if (timeoutId) clearTimeout(timeoutId);
          resolve(voices);
        }
      };
      
      // Set handler
      this.synthesis.onvoiceschanged = onVoicesChanged;
      
      // Timeout sau 3 giây nếu vẫn chưa có voices
      timeoutId = setTimeout(() => {
        voices = this.synthesis.getVoices();
        this.synthesis.onvoiceschanged = null;
        resolve(voices); // Trả về dù có hay không
      }, 3000);
    });
  }

  // Dispose
  dispose() {
    this.stopListening();
    this.stopSpeaking();
  }
}

// Export singleton instance
export default new VoiceService();

